import { tr } from '@/lib/i18n-core';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadGet, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime, humanize, money, todayIso } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, Checkbox, DatePicker, Field, Switch, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import {
  AUTHORITY_KIND_INFO,
  PERSON_AUTHORITY_KINDS,
  THRESHOLD_AUTHORITY_KINDS,
  type AuthorityKind,
  type GrantView,
  type HospitalAuthorityView,
  type Page,
  type PersonRow,
  type PersonAuthorityKind,
} from './accounts-types';
import { thresholdLabel, valueLabel } from './accounts-api';
import { authorityChangePath, type ChangeTarget } from './AuthorityChangePage';

interface Summary {
  enabledKinds: AuthorityKind[];
  backfilled: boolean;
  hospital: { id: string; kind: AuthorityKind; branchId: string | null; branchName: string | null; valueMinor: number | null; noLimit: boolean; flag: boolean | null }[];
  legacyHospital: HospitalAuthorityView | null;
  pendingCount: number;
}

interface RoleRow {
  id: string;
  code: string;
  name: string;
  startingValues: GrantView[];
  pending: GrantView[];
}

interface DiscountRequest {
  id: string;
  number: string;
  context: string;
  netMinor: number;
  operatorDiscountMinor: number;
  operatorDiscountPct: number;
  discountReason: string | null;
  discountRequestedAt: string;
  requestedByName: string | null;
  walkInName: string | null;
  patient: { fullName: string; mrn: string } | null;
}

const APPROVER_CEILINGS: PersonAuthorityKind[] = ['supplier_payment_approve_minor', 'stock_writeoff_approve_minor', 'pos_return_approve_minor', 'claim_adjustment_approve_minor'];

function grantValueText(row: GrantView): string {
  const info = AUTHORITY_KIND_INFO[row.kind];
  if (!info) return '';
  if (info.value === 'flag') return row.flag ? 'On' : 'Off';
  if (info.value === 'pct') return `${row.valuePct ?? 0}%`;
  if (row.noLimit) return info.subject === 'hospital' ? 'No threshold' : 'No limit';
  return money(row.valueMinor ?? 0);
}

function statusBadge(status: string) {
  const variant = status === 'active' ? 'success' : status === 'pending' ? 'warning' : status === 'rejected' ? 'destructive' : 'muted';
  return <Badge variant={variant}>{status === 'pending' ? tr('billing', 'waiting_for_approval_2') : humanize(status)}</Badge>;
}

const TABS = ['people', 'roles', 'thresholds', 'waiting', 'history', 'reports'];

function useAuthorityScreen(tab: string) {
  const navigate = useNavigate();
  const returnTo = `/accounts/authority?tab=${tab}`;
  return (target: ChangeTarget, kinds: AuthorityKind[], withBranches = false) => navigate(authorityChangePath(target, kinds, returnTo, withBranches));
}

export function FinancialAuthorityPage() {
  const { can } = useAuth();
  const manage = can('authority.manage');
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = params.get('tab');
    return wanted && TABS.includes(wanted) ? wanted : 'people';
  });
  const summary = useQuery({ queryKey: ['authority-summary'], queryFn: () => get<Summary>('/accounts/authority/summary') });
  const enabled = summary.data?.enabledKinds ?? [];
  const thresholdsOn = enabled.some((kind) => (THRESHOLD_AUTHORITY_KINDS as readonly string[]).includes(kind));

  const tabs = [
    { id: 'people', label: tr('billing', 'people') },
    { id: 'roles', label: tr('billing', 'starting_values_by_role') },
    ...(thresholdsOn ? [{ id: 'thresholds', label: tr('billing', 'approval_thresholds') }] : []),
    {
      id: 'waiting',
      label: tr('billing', 'waiting_for_approval'),
      badge: summary.data?.pendingCount ? <Badge variant="warning">{summary.data.pendingCount}</Badge> : undefined,
    },
    { id: 'history', label: tr('billing', 'history') },
    ...(can('report.financial') ? [{ id: 'reports', label: tr('billing', 'reports') }] : []),
  ];

  return (
    <>
      <PageHeader
        title={tr('billing', 'financial_authority')}
        description={tr('billing', 'who_may_give_a_discount_approve_a_refund_or_a')}
      />
      {summary.data && !summary.data.backfilled && (
        <Alert variant="warning" className="mb-4">
          {tr('billing', 'the_limits_from_the_staff_screens_are_still')}
        </Alert>
      )}
      <TwoPersonCards summary={summary.data} manage={manage && Boolean(summary.data)} />
      <Tabs active={tab} onChange={setTab} tabs={tabs} />
      {tab === 'people' && <PeopleTab enabled={enabled} manage={manage} />}
      {tab === 'roles' && <RolesTab enabled={enabled} manage={manage} />}
      {tab === 'thresholds' && <ThresholdsTab summary={summary.data} manage={manage} />}
      {tab === 'waiting' && <WaitingTab manage={manage} />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'reports' && <ReportsTab />}
    </>
  );
}

function TwoPersonCards({ summary, manage }: { summary: Summary | undefined; manage: boolean }) {
  const { user } = useAuth();
  const change = useAuthorityScreen('people');
  const flagOf = (kind: AuthorityKind) => summary?.hospital.find((row) => row.kind === kind)?.flag === true;
  const cards: { kind: AuthorityKind; title: string; text: string }[] = [
    { kind: 'dual_control_authority', title: tr('billing', 'two_person_rule_for_authority_changes'), text: 'Every change to a limit or threshold waits for a second person to approve it.' },
    { kind: 'dual_control_share', title: tr('billing', 'two_person_rule_for_doctor_share_changes'), text: 'Every change to a doctor share waits for a second person to approve it.' },
  ];
  return (
    <div className="mb-4 grid gap-3 md:grid-cols-2">
      {cards.map((card) => (
        <Card key={card.kind} className="flex items-start justify-between gap-4 p-4">
          <div>
            <p className="font-medium">{card.title}</p>
            <p className="text-sm text-muted-foreground">{card.text}</p>
          </div>
          <Switch
            checked={flagOf(card.kind)}
            disabled={!manage || !user?.isSuperAdmin}
            onCheckedChange={() => change({ title: AUTHORITY_KIND_INFO[card.kind].label }, [card.kind])}
            aria-label={card.title}
          />
        </Card>
      ))}
    </div>
  );
}

function PeopleTab({ enabled, manage }: { enabled: AuthorityKind[]; manage: boolean }) {
  const change = useAuthorityScreen('people');
  const [search, setSearch] = useState('');
  const [everyone, setEveryone] = useState(false);
  const [showCeilings, setShowCeilings] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState<PersonRow | null>(null);
  const people = useQuery({
    queryKey: ['authority-people', search, everyone, cursor],
    queryFn: () =>
      get<Page<PersonRow> & { enabledKinds: AuthorityKind[] }>(
        `/accounts/authority/people${qs({ q: search || undefined, handlesMoney: everyone ? '0' : '1', cursor, limit: 50 })}`,
      ),
  });
  const personKinds = useMemo(
    () =>
      PERSON_AUTHORITY_KINDS.filter((kind) => enabled.includes(kind)).filter(
        (kind) => showCeilings || !APPROVER_CEILINGS.includes(kind),
      ),
    [enabled, showCeilings],
  );

  return (
    <>
      <SearchFilterBar
        search={{ value: search, onChange: (value) => { setSearch(value); setCursor(undefined); }, placeholder: tr('billing', 'search_staff') }}
        filters={
          <FilterField><div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Checkbox checked={everyone} onCheckedChange={(value) => { setEveryone(value); setCursor(undefined); }} label={tr('billing', 'show_everyone_not_only_staff_who_handle_money')} />
            <Checkbox checked={showCeilings} onCheckedChange={setShowCeilings} label={tr('billing', 'show_approval_ceilings')} />
          </div></FilterField>
        }
        trailing={
          manage &&
          selected.length > 0 && (
            <Button
              size="sm"
              onClick={() =>
                change({ userIds: selected, title: tr('billing', 'set_a_limit_for_x_people', { length: selected.length }) }, PERSON_AUTHORITY_KINDS.filter((kind) => enabled.includes(kind)))
              }
            >
              {tr('billing', 'set_for')} {selected.length} {tr('billing', 'selected')}
            </Button>
          )
        }
        onReset={
          search || everyone || showCeilings
            ? () => {
                setSearch('');
                setEveryone(false);
                setShowCeilings(false);
                setCursor(undefined);
              }
            : undefined
        }
      />
      <DataTable
        rows={people.data?.data}
        loading={people.isLoading} refreshing={people.isFetching}
        error={people.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row)}
        empty={tr('billing', 'nobody_here_handles_money_yet')}
        footer={
          cursor || people.data?.nextCursor ? (
            <div className="ms-auto flex gap-2">
              {cursor && <Button variant="outline" size="sm" onClick={() => setCursor(undefined)}>{tr('billing', 'first_page')}</Button>}
              {people.data?.nextCursor && <Button variant="outline" size="sm" onClick={() => setCursor(people.data?.nextCursor ?? undefined)}>{tr('billing', 'next_page')}</Button>}
            </div>
          ) : undefined
        }
        columns={[
          ...(manage
            ? [
                {
                  header: '',
                  width: '36px',
                  cell: (row: PersonRow) => (
                    <span onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onCheckedChange={(value) => setSelected((current) => (value ? [...current, row.id] : current.filter((id) => id !== row.id)))}
                        aria-label={tr('billing', 'select_x', { name: row.name })}
                      />
                    </span>
                  ),
                },
              ]
            : []),
          {
            header: tr('billing', 'person'),
            sort: (row: PersonRow) => row.name,
            cell: (row: PersonRow) => (
              <div>
                <div className="font-medium">{row.name}</div>
                <div className="text-xs text-muted-foreground">{row.roles.map((role) => role.name).join(', ') || row.username}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {row.isSuperAdmin && <Badge variant="secondary">{tr('billing', 'super_admin_no_limit')}</Badge>}
                  {row.pending.length > 0 && <Badge variant="warning">{tr('billing', 'waiting_for_approval_2')}</Badge>}
                </div>
              </div>
            ),
          },
          ...personKinds.map((kind) => ({
            header: AUTHORITY_KIND_INFO[kind].label,
            align: 'right' as const,
            cell: (row: PersonRow) => {
              const value = row.values.find((candidate) => candidate.kind === kind);
              if (!value) return '—';
              if (row.isSuperAdmin) return <span className="text-muted-foreground">{tr('billing', 'no_limit')}</span>;
              return <span className={value.dormant ? 'text-muted-foreground line-through' : ''}>{valueLabel(kind, value)}</span>;
            },
          })),
        ]}
      />
      {open && <PersonDialog person={open} kinds={personKinds} manage={manage} onClose={() => setOpen(null)} />}
    </>
  );
}

function PersonDialog({ person, kinds, manage, onClose }: { person: PersonRow; kinds: PersonAuthorityKind[]; manage: boolean; onClose: () => void }) {
  const change = useAuthorityScreen('people');
  const detail = useQuery({
    queryKey: ['authority-person', person.id],
    queryFn: () => get<PersonRow & { history: GrantView[] }>(`/accounts/authority/people/${person.id}`),
  });
  const current = detail.data ?? person;
  return (
    <Dialog open onClose={onClose} title={person.name} description={tr('billing', 'current_values_and_recent_changes')} wide>
      <div className="space-y-4">
        <DataTable
          rows={current.values.filter((value) => kinds.includes(value.kind))}
          rowKey={(row) => row.kind}
          dense
          columns={[
            { header: tr('billing', 'limit'), cell: (row) => AUTHORITY_KIND_INFO[row.kind].label },
            {
              header: tr('billing', 'value'),
              align: 'right',
              cell: (row) => (current.isSuperAdmin ? 'no limit (Super Admin)' : valueLabel(row.kind, row)),
            },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                manage && !current.isSuperAdmin ? (
                  <Button size="sm" variant="outline" onClick={() => change({ userId: person.id, title: `${AUTHORITY_KIND_INFO[row.kind].label} — ${person.name}` }, [row.kind])}>
                    {tr('billing', 'change')}
                  </Button>
                ) : null,
            },
          ]}
        />
        <div>
          <p className="mb-2 text-sm font-medium">{tr('billing', 'recent_changes')}</p>
          <DataTable
            rows={detail.data?.history}
            loading={detail.isLoading} refreshing={detail.isFetching}
            rowKey={(row) => row.id}
            dense
            empty={tr('billing', 'no_changes_yet')}
            columns={[
              { header: tr('billing', 'when'), cell: (row) => dateTime(row.requestedAt) },
              { header: tr('billing', 'limit'), cell: (row) => AUTHORITY_KIND_INFO[row.kind]?.label ?? row.kind },
              { header: tr('billing', 'value'), align: 'right', cell: (row) => grantValueText(row) },
              { header: tr('billing', 'status'), cell: (row) => statusBadge(row.status) },
              { header: tr('billing', 'reason'), cell: (row) => row.reason },
              { header: tr('billing', 'by_2'), cell: (row) => row.requestedByName ?? '—' },
            ]}
          />
        </div>
      </div>
    </Dialog>
  );
}

function RolesTab({ enabled, manage }: { enabled: AuthorityKind[]; manage: boolean }) {
  const change = useAuthorityScreen('roles');
  const roles = useQuery({ queryKey: ['authority-roles'], queryFn: () => get<RoleRow[]>('/accounts/authority/roles') });
  const kinds = PERSON_AUTHORITY_KINDS.filter((kind) => enabled.includes(kind));
  return (
    <>
      <p className="mb-3 text-sm text-muted-foreground">
        {tr('billing', 'when_someone_first_receives_a_role_they_are')}
      </p>
      <DataTable
        rows={roles.data}
        loading={roles.isLoading} refreshing={roles.isFetching}
        error={roles.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_roles')}
        columns={[
          { header: tr('billing', 'role'), sort: (row) => row.name, cell: (row) => row.name },
          {
            header: tr('billing', 'starting_values'),
            cell: (row) =>
              row.startingValues.length === 0 ? (
                <span className="text-muted-foreground">{tr('billing', 'none')}</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {row.startingValues.map((grant) => (
                    <Badge key={grant.id} variant="secondary">
                      {AUTHORITY_KIND_INFO[grant.kind]?.label}: {grantValueText(grant)}
                    </Badge>
                  ))}
                  {row.pending.length > 0 && <Badge variant="warning">{tr('billing', 'waiting_for_approval_2')}</Badge>}
                </div>
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              manage ? (
                <Button size="sm" variant="outline" onClick={() => change({ roleId: row.id, title: tr('billing', 'starting_value_x', { name: row.name }) }, kinds)}>
                  {tr('billing', 'set_a_starting_value')}
                </Button>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function ThresholdsTab({ summary, manage }: { summary: Summary | undefined; manage: boolean }) {
  const { user } = useAuth();
  const change = useAuthorityScreen('thresholds');
  const kinds = THRESHOLD_AUTHORITY_KINDS.filter((kind) => summary?.enabledKinds.includes(kind));
  const legacy = summary?.legacyHospital;
  const legacyValue = (kind: AuthorityKind) =>
    kind === 'supplier_payment_threshold_minor' ? legacy?.supplierPaymentMinor : kind === 'stock_writeoff_threshold_minor' ? legacy?.stockAdjustMinor : legacy?.posReturnMinor;
  return (
    <>
      <p className="mb-3 text-sm text-muted-foreground">{tr('billing', 'no_threshold_nothing_needs_a_second_person_0')}</p>
      <DataTable
        rows={kinds}
        rowKey={(kind) => kind}
        columns={[
          { header: tr('billing', 'rule'), sort: (kind) => AUTHORITY_KIND_INFO[kind].label, cell: (kind) => AUTHORITY_KIND_INFO[kind].label },
          {
            header: tr('billing', 'every_branch'),
            align: 'right',
            cell: (kind) => {
              if (!summary?.backfilled) return thresholdLabel(legacyValue(kind) ?? null);
              const row = summary.hospital.find((candidate) => candidate.kind === kind && !candidate.branchId);
              return row ? (row.noLimit ? 'No threshold' : money(row.valueMinor ?? 0)) : 'No threshold';
            },
          },
          {
            header: tr('billing', 'branch_figures'),
            cell: (kind) =>
              (summary?.hospital ?? [])
                .filter((row) => row.kind === kind && row.branchId)
                .map((row) => `${row.branchName ?? 'Branch'}: ${row.noLimit ? 'No threshold' : money(row.valueMinor ?? 0)}`)
                .join(' · ') || '—',
          },
          {
            header: '',
            align: 'right',
            cell: (kind) =>
              manage && user?.isSuperAdmin ? (
                <Button size="sm" variant="outline" onClick={() => change({ title: AUTHORITY_KIND_INFO[kind].label }, [kind], true)}>
                  {tr('billing', 'change')}
                </Button>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function DecisionDialog({ title, onClose, onDecide }: { title: string; onClose: () => void; onDecide: (note: string) => Promise<unknown> }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const run = useMutation({ mutationFn: () => onDecide(note.trim()), onSuccess: onClose, onError: (caught) => setError(describeError(caught)) });
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button onClick={() => run.mutate()} loading={run.isPending}>
            {tr('billing', 'confirm')}
          </Button>
        </>
      }
    >
      <Field label={tr('billing', 'note')} hint={tr('billing', 'optional')}>
        <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
      {error && <Alert variant="destructive" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}

function WaitingTab({ manage }: { manage: boolean }) {
  const { can, user, hasModule } = useAuth();
  const approvesDiscounts = can('discount.approve') && hasModule('billing');
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<{ title: string; run: (note: string) => Promise<unknown> } | null>(null);
  const changes = useQuery({
    queryKey: ['authority-changes', 'pending'],
    queryFn: () => get<Page<GrantView>>(`/accounts/authority/changes${qs({ status: 'pending', limit: 100 })}`),
  });
  const discounts = useQuery({
    queryKey: ['discount-requests'],
    queryFn: () => get<Page<DiscountRequest>>('/invoices/discount-requests'),
    enabled: approvesDiscounts,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['authority-changes'] });
    void queryClient.invalidateQueries({ queryKey: ['authority-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['discount-requests'] });
  };
  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 font-medium">{tr('billing', 'authority_changes')}</p>
        <DataTable
          rows={changes.data?.data}
          loading={changes.isLoading} refreshing={changes.isFetching}
          error={changes.error}
          rowKey={(row) => row.id}
          empty={tr('billing', 'nothing_is_waiting')}
          columns={[
            { header: tr('billing', 'asked'), sort: (row) => row.requestedAt, cell: (row) => dateTime(row.requestedAt) },
            { header: tr('billing', 'change'), cell: (row) => `${AUTHORITY_KIND_INFO[row.kind]?.label ?? row.kind}${row.userName ? ` — ${row.userName}` : row.roleName ? ` — ${row.roleName}` : row.branchName ? ` — ${row.branchName}` : ''}` },
            { header: tr('billing', 'new_value'), align: 'right', cell: (row) => grantValueText(row) },
            { header: tr('billing', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason },
            { header: tr('billing', 'asked_by'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <div className="flex justify-end gap-2">
                  {manage && row.requestedBy !== user?.id && (
                    <>
                      <Button size="sm" onClick={() => setDeciding({ title: tr('billing', 'approve_this_change'), run: (note) => post(`/accounts/authority/changes/${row.id}/approve`, { note: note || undefined }).then(refresh) })}>
                        {tr('billing', 'approve')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeciding({ title: tr('billing', 'reject_this_change'), run: (note) => post(`/accounts/authority/changes/${row.id}/reject`, { note: note || undefined }).then(refresh) })}>
                        {tr('billing', 'reject')}
                      </Button>
                    </>
                  )}
                  {row.requestedBy === user?.id && (
                    <Button size="sm" variant="ghost" onClick={() => setDeciding({ title: tr('billing', 'withdraw_your_change'), run: () => post(`/accounts/authority/changes/${row.id}/cancel`, {}).then(refresh) })}>
                      {tr('billing', 'withdraw')}
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
      {approvesDiscounts && (
        <div>
          <p className="mb-2 font-medium">{tr('billing', 'discounts_sent_for_approval')}</p>
          <DataTable
            rows={discounts.data?.data}
            loading={discounts.isLoading} refreshing={discounts.isFetching}
            error={discounts.error}
            rowKey={(row) => row.id}
            empty={tr('billing', 'no_discount_is_waiting')}
            columns={[
              { header: tr('billing', 'estimate'), sort: (row) => row.number, cell: (row) => row.number },
              { header: tr('billing', 'patient'), sort: (row) => row.patient?.fullName ?? row.walkInName, cell: (row) => row.patient?.fullName ?? row.walkInName ?? '—' },
              { header: tr('billing', 'discount'), align: 'right', sort: (row) => row.operatorDiscountMinor, cell: (row) => `${money(row.operatorDiscountMinor)} (${row.operatorDiscountPct}%)` },
              { header: tr('billing', 'reason'), sort: (row) => row.discountReason, cell: (row) => row.discountReason ?? '—' },
              { header: tr('billing', 'asked_by'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
              {
                header: '',
                align: 'right',
                cell: (row) => (
                  <div className="flex justify-end gap-2">
                    <Button size="sm" onClick={() => setDeciding({ title: tr('billing', 'approve_this_discount'), run: (note) => post(`/invoices/${row.id}/discount/approve`, { note: note || undefined }).then(refresh) })}>
                      {tr('billing', 'approve')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDeciding({ title: tr('billing', 'send_this_discount_back'), run: (note) => post(`/invoices/${row.id}/discount/reject`, { note: note || undefined }).then(refresh) })}>
                      {tr('billing', 'send_back')}
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}
      {deciding && <DecisionDialog title={deciding.title} onClose={() => setDeciding(null)} onDecide={deciding.run} />}
    </div>
  );
}

function HistoryTab() {
  const toast = useToast();
  const [carriedOver, setCarriedOver] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const filter = { source: carriedOver ? 'carried_over' : undefined };
  const history = useQuery({
    queryKey: ['authority-changes', 'history', carriedOver, cursor],
    queryFn: () => get<Page<GrantView>>(`/accounts/authority/changes${qs({ ...filter, cursor, limit: 50 })}`),
  });
  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField><Checkbox checked={carriedOver} onCheckedChange={(value) => { setCarriedOver(value); setCursor(undefined); }} label={tr('billing', 'carried_over_review')} /></FilterField>
        }
        trailing={
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadGet(`/accounts/authority/changes.csv${qs(filter)}`, 'authority-changes.csv').catch((error) => toast.error(tr('billing', 'could_not_download'), describeError(error)))}
          >
            {tr('billing', 'download_csv')}
          </Button>
        }
        onReset={
          carriedOver
            ? () => {
                setCarriedOver(false);
                setCursor(undefined);
              }
            : undefined
        }
      />
      <DataTable
        rows={history.data?.data}
        loading={history.isLoading} refreshing={history.isFetching}
        error={history.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_changes_yet')}
        footer={
          cursor || history.data?.nextCursor ? (
            <div className="ms-auto flex gap-2">
              {cursor && <Button variant="outline" size="sm" onClick={() => setCursor(undefined)}>{tr('billing', 'first_page')}</Button>}
              {history.data?.nextCursor && <Button variant="outline" size="sm" onClick={() => setCursor(history.data?.nextCursor ?? undefined)}>{tr('billing', 'next_page')}</Button>}
            </div>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'when'), sort: (row) => row.requestedAt, cell: (row) => dateTime(row.requestedAt) },
          { header: tr('billing', 'setting'), sort: (row) => AUTHORITY_KIND_INFO[row.kind]?.label ?? row.kind, cell: (row) => AUTHORITY_KIND_INFO[row.kind]?.label ?? row.kind },
          { header: tr('billing', 'for'), sort: (row) => row.userName ?? row.roleName ?? row.branchName, cell: (row) => row.userName ?? row.roleName ?? row.branchName ?? 'Hospital' },
          { header: tr('billing', 'value'), align: 'right', cell: (row) => grantValueText(row) },
          { header: tr('billing', 'status'), sort: (row) => row.status, cell: (row) => statusBadge(row.status) },
          { header: tr('billing', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason },
          { header: tr('billing', 'asked_by'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
          { header: tr('billing', 'decided_by'), sort: (row) => row.decidedByName, cell: (row) => row.decidedByName ?? '—' },
        ]}
      />
    </>
  );
}

function ReportsTab() {
  const toast = useToast();
  const [from, setFrom] = useState(todayIso().slice(0, 8) + '01');
  const [to, setTo] = useState(todayIso());
  const discounts = useQuery({
    queryKey: ['accounts-report-discounts', from, to],
    queryFn: () =>
      get<Page<{ givenBy: { name: string | null } | null; approvedBy: { name: string | null } | null; bills: number; lines: number; discountMinor: number; grossMinor: number }>>(
        `/accounts/reports/discounts${qs({ from, to, limit: 100 })}`,
      ),
  });
  const approvals = useQuery({
    queryKey: ['accounts-report-approvals', from, to],
    queryFn: () =>
      get<Page<{ kind: string; id: string; at: string; amountMinor: number; reference: string | null; requestedBy: { name: string | null } | null; approvedBy: { name: string | null } | null }>>(
        `/accounts/reports/approvals${qs({ from, to, limit: 100 })}`,
      ),
  });
  const defaultFrom = todayIso().slice(0, 8) + '01';
  return (
    <div className="space-y-6">
      <SearchFilterBar
        className="mb-0"
        filters={
          <>
            <FilterField label={tr('billing', 'from')}>
              <DatePicker value={from} onChange={(value) => setFrom(value)} className="w-40" />
            </FilterField>
            <FilterField label={tr('billing', 'to_2')}>
              <DatePicker value={to} onChange={(value) => setTo(value)} className="w-40" />
            </FilterField>
          </>
        }
        trailing={
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadGet('/accounts/authority/matrix.csv', 'authority-limits.csv').catch((error) => toast.error(tr('billing', 'could_not_download'), describeError(error)))}
          >
            {tr('billing', 'current_limits_csv')}
          </Button>
        }
        onReset={
          from !== defaultFrom || to !== todayIso()
            ? () => {
                setFrom(defaultFrom);
                setTo(todayIso());
              }
            : undefined
        }
      />
      <div>
        <p className="mb-2 font-medium">{tr('billing', 'discounts_given')}</p>
        <DataTable
          rows={discounts.data?.data}
          loading={discounts.isLoading} refreshing={discounts.isFetching}
          error={discounts.error}
          rowKey={(row) => `${row.givenBy?.name}-${row.approvedBy?.name}`}
          empty={tr('billing', 'no_discounts_in_this_period')}
          columns={[
            { header: tr('billing', 'given_by'), sort: (row) => row.givenBy?.name, cell: (row) => row.givenBy?.name ?? '—' },
            { header: tr('billing', 'approved_by'), sort: (row) => row.approvedBy?.name, cell: (row) => row.approvedBy?.name ?? 'within their own limit' },
            { header: tr('billing', 'bills'), align: 'right', sort: (row) => row.bills, cell: (row) => row.bills },
            { header: tr('billing', 'discount'), align: 'right', sort: (row) => row.discountMinor, cell: (row) => money(row.discountMinor) },
          ]}
        />
      </div>
      <div>
        <p className="mb-2 font-medium">{tr('billing', 'released_by_a_second_person')}</p>
        <DataTable
          rows={approvals.data?.data}
          loading={approvals.isLoading} refreshing={approvals.isFetching}
          error={approvals.error}
          rowKey={(row) => `${row.kind}-${row.id}`}
          empty={tr('billing', 'nothing_in_this_period')}
          columns={[
            { header: tr('billing', 'when'), sort: (row) => row.at, cell: (row) => dateOnly(row.at) },
            { header: tr('billing', 'what'), sort: (row) => row.kind, cell: (row) => humanize(row.kind) },
            { header: tr('billing', 'reference'), sort: (row) => row.reference, cell: (row) => row.reference ?? '—' },
            { header: tr('billing', 'amount'), align: 'right', sort: (row) => row.amountMinor, cell: (row) => money(row.amountMinor) },
            { header: tr('billing', 'asked_by'), sort: (row) => row.requestedBy?.name, cell: (row) => row.requestedBy?.name ?? '—' },
            { header: tr('billing', 'approved_by'), sort: (row) => row.approvedBy?.name, cell: (row) => row.approvedBy?.name ?? '—' },
          ]}
        />
      </div>
    </div>
  );
}
